import clsx from 'clsx';
import { forwardRef, useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { type Components as ReactMarkdownComponents } from 'react-markdown/src/ast-to-react';
import gfm from 'remark-gfm';

import type {
  DataTestId,
  MaskingProps,
  StylingProps,
} from '@dynatrace/strato-components/core';

import { MarkdownWrapperCSS } from './Markdown.sty.js';
import { tableMappings } from './transformers/table-mapping.js';
import { typographyMappings } from './transformers/typography-mapping.js';
import { findValidParent, findOffset, extractSourcePos } from './utils.js';
import { type Components } from '../../core/types/markdown/react-markdown.js';
import { textStyleCSS } from '../../styles/textStyle.sty.js';

/**
 * @public
 */
export interface MarkdownSelection {
  start: MarkdownSelectionPoint;
  end: MarkdownSelectionPoint;
}

/**
 * @public
 */
export interface MarkdownSelectionPoint {
  line: number;
  column: number;
  offset: number;
}

/**
 * @public
 */
export const componentMappings: Components = {
  ...typographyMappings,
  ...tableMappings,
};

/**
 * @public
 */
export interface MarkdownProps extends MaskingProps, DataTestId, StylingProps {
  /** The markdown content itself. */
  children: string;
  /**
   * A map of HTML tags (e.g., 'a', 'h1') to functions that provide custom implementations,
   * allowing for the override of default rendering.
   * @defaultValue undefined
   */
  customComponentMappings?: Components;
  onSelectionChange?: (selection: MarkdownSelection) => void;
}

/**
 * The `Markdown` component is a read-only way to display content formatted in
 * markdown-style.
 * @public
 */
export const Markdown = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MarkdownProps
>(
  (
    {
      children,
      onSelectionChange,
      customComponentMappings,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
    },
    forwardedRef,
  ) => {
    const extendedComponentMappings = useMemo(() => {
      return customComponentMappings
        ? {
            ...componentMappings,
            ...customComponentMappings,
          }
        : componentMappings;
    }, [customComponentMappings]);

    const startNode = useRef<Element | null>(null);
    const endNode = useRef<Element | null>(null);

    const handleSelectionChange = () => {
      const selection = document.getSelection();

      if (
        !selection ||
        selection.isCollapsed ||
        !selection.anchorNode ||
        !selection.focusNode
      ) {
        return;
      }

      const start = findValidParent(selection.anchorNode);
      const end = findValidParent(selection.focusNode);

      if (!start && !end) {
        return;
      }

      if (start) {
        startNode.current = start;
      }
      if (end) {
        endNode.current = end;
      }

      if (startNode.current && endNode.current) {
        const sourcePos = extractSourcePos(startNode.current, endNode.current);
        if (sourcePos && onSelectionChange) {
          const startOffset = findOffset(
            sourcePos.start,
            children as string,
            -1,
          );
          const endOffset = findOffset(sourcePos?.end, children as string);
          sourcePos.start.offset = startOffset;
          sourcePos.end.offset = endOffset;

          onSelectionChange(sourcePos);
        }
      }
    };

    useEffect(() => {
      document.addEventListener('selectionchange', handleSelectionChange);

      return () => {
        document.removeEventListener('selectionchange', handleSelectionChange);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [children]);

    return (
      <div
        className={clsx(
          textStyleCSS({ textStyle: 'base' }),
          MarkdownWrapperCSS,
          consumerClassName,
        )}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        ref={forwardedRef}
      >
        <ReactMarkdown
          components={extendedComponentMappings as ReactMarkdownComponents}
          remarkPlugins={[gfm as never]}
          skipHtml={true}
          sourcePos
        >
          {children}
        </ReactMarkdown>
      </div>
    );
  },
);

(
  Markdown as typeof Markdown & {
    displayName: string;
  }
).displayName = 'Markdown';
